import { IScope, ILocationService, ITimeoutService, IPromise } from 'angular';
import { IResponseSupportBridgeClient } from '../../serviceworkerBridgeClients/IResponseSupportBridgeClient';

/**
 * ResponseSupportController - Controller for the response support tool.
 * This tool helps diagnose failed exam response uploads by listing all stored responses
 * and displaying the JSON that would be produced by createResponseDataJson.
 */
export class ResponseSupportController {
    public responses: Array<{ userUUID: string; loading?: boolean; error?: string }> = [];
    public selectedResponse: string | null = null;
    public responseJson: string = '';
    public loading: boolean = false;
    public loadingJson: boolean = false;
    public sendingDataPackage: boolean = false;
    public error: string | null = null;
    public successMessage: string | null = null;
    public showMessageModal: boolean = false;
    public supportMessage: string = '';
    public deviceId: string | null = null;
    public pendingRecoveries: any[] = [];
    public recoveredResponses: any[] = [];
    public loadingRecoveries: boolean = false;
    public retryingId: number | null = null;
    public retryingAll: boolean = false;
    private loadingResponsesPromise: Promise<void> | null = null;
    private pendingTimeouts: IPromise<any>[] = [];

    static $inject = [
        '$scope',
        '$location',
        '$timeout',
        'ResponseSupportBridgeClient'
    ];

    constructor(
        private $scope: IScope,
        private $location: ILocationService,
        private $timeout: ITimeoutService,
        private ResponseSupportBridgeClient: IResponseSupportBridgeClient
    ) {
        // Set log identifier for debugging (cast to any to avoid TypeScript error)
        (this.$scope as any)._logIdentifier = 'ResponseSupportController';
        this.loadDeviceId();
        this.loadAllResponses();
        this.loadPendingRecoveries();

        // Per-entry success/failure is already conveyed by the promise returned
        // from retry{,All}PendingRecovery(), and the completion handlers below
        // refresh the list once per operation. No additional listener wiring is
        // needed here (PWAController owns the app-wide auto-retry listeners).

        // Cancel pending timeouts when the scope is destroyed.
        this.$scope.$on('$destroy', () => {
            this.cancelPendingTimeouts();
        });
    }

    /** Track a $timeout promise for cancellation on $destroy. */
    private trackTimeout(fn: () => void, delay?: number): IPromise<any> {
        const p = this.$timeout(fn, delay);
        this.pendingTimeouts.push(p);
        p.finally(() => {
            const idx = this.pendingTimeouts.indexOf(p);
            if (idx !== -1) this.pendingTimeouts.splice(idx, 1);
        });
        return p;
    }

    private cancelPendingTimeouts(): void {
        for (const p of this.pendingTimeouts) {
            this.$timeout.cancel(p);
        }
        this.pendingTimeouts = [];
    }

    /**
     * Reads the device ID from the cookie.
     */
    private loadDeviceId(): void {
        const cookieName = 'ib_device_id';
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === cookieName && value) {
                this.deviceId = value;
                console.log('ResponseSupportController: Found device ID:', value);
                return;
            }
        }
        console.log('ResponseSupportController: No device ID cookie found');
    }

    /**
     * Loads all stored responses from IndexedDB.
     */
    async loadAllResponses(): Promise<void> {
        // Prevent multiple concurrent calls
        if (this.loadingResponsesPromise) {
            return this.loadingResponsesPromise;
        }

        this.loadingResponsesPromise = (async () => {
            this.loading = true;
            this.error = null;
            this.successMessage = null;

            try {
                const userUUIDs = await this.ResponseSupportBridgeClient.listAllResponses();

                // $timeout automatically triggers a digest cycle
                this.trackTimeout(() => {
                    this.responses = userUUIDs.map(uuid => ({ userUUID: uuid }));

                    if (userUUIDs.length === 0) {
                        this.error = 'No stored responses found.';
                    }
                    this.loading = false;
                });
            } catch (err) {
                this.trackTimeout(() => {
                    this.error = err instanceof Error ? err.message : 'Failed to load responses';
                    this.loading = false;
                    console.error('Error loading responses:', err);
                });
            } finally {
                this.loadingResponsesPromise = null;
            }
        })();

        return this.loadingResponsesPromise;
    }

    /**
     * Loads the response data JSON for a specific userUUID.
     * @param userUUID - The user UUID to load response data for
     */
    async loadResponseJson(userUUID: string): Promise<void> {
        // Clear the previous JSON immediately to ensure the panel is removed
        this.responseJson = '';
        this.selectedResponse = null;
        this.loadingJson = true;
        this.error = null;
        this.successMessage = null;

        // Mark the response as loading
        const response = this.responses.find(r => r.userUUID === userUUID);
        if (response) {
            response.loading = true;
            response.error = undefined;
        }

        try {
            console.log("ResponseSupportController: Requesting JSON for userUUID", userUUID);
            const json = await this.ResponseSupportBridgeClient.getResponseDataJson(userUUID);
            console.log("ResponseSupportController: Received JSON, length:", json?.length);

            const jsonValue = json || '';
            const responseItem = response;

            // $timeout automatically triggers a digest cycle — no need for manual $apply
            this.trackTimeout(() => {
                this.responseJson = jsonValue;
                this.selectedResponse = userUUID;

                if (responseItem) {
                    responseItem.loading = false;
                }
                this.loadingJson = false;
            });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load response JSON';

            this.trackTimeout(() => {
                this.error = errorMessage;

                if (response) {
                    response.loading = false;
                    response.error = errorMessage;
                }
                this.loadingJson = false;

                console.error(`Error loading response JSON for ${userUUID}:`, err);
            });
        }
    }

    /**
     * Copies the response JSON to clipboard.
     */
    async copyToClipboard(): Promise<void> {
        if (!this.responseJson) {
            return;
        }

        try {
            // Type assertion for browser Navigator (not WorkerNavigator)
            const browserNavigator = navigator as Navigator & { clipboard?: Clipboard };
            if (browserNavigator.clipboard && browserNavigator.clipboard.writeText) {
                await browserNavigator.clipboard.writeText(this.responseJson);
                console.log('Response JSON copied to clipboard');
                return;
            }
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }

        // Fallback: select the text
        const textArea = document.createElement('textarea');
        textArea.value = this.responseJson;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            console.log('Response JSON copied to clipboard (fallback)');
        } catch (fallbackErr) {
            console.error('Fallback copy failed:', fallbackErr);
        }
        document.body.removeChild(textArea);
    }

    /**
     * Opens the modal to collect support message before sending data package.
     */
    openSupportMessageModal(): void {
        if (this.responses.length === 0) {
            this.trackTimeout(() => {
                this.error = 'No stored responses found to send.';
                this.successMessage = null;
            });
            return;
        }

        const Sentry = (window as any).Sentry;
        if (!Sentry) {
            this.trackTimeout(() => {
                this.error = 'Sentry is not available. Cannot send data package.';
                this.successMessage = null;
            });
            return;
        }

        // Reset message and show modal
        this.supportMessage = '';
        this.showMessageModal = true;
        this.error = null;
        this.successMessage = null;

        // Focus the textarea after modal is shown — $timeout triggers digest automatically
        this.trackTimeout(() => {
            const textarea = document.getElementById('supportMessage');
            if (textarea) {
                textarea.focus();
            }
        }, 100);
    }

    /**
     * Closes the support message modal without sending.
     */
    cancelSupportMessage(): void {
        this.showMessageModal = false;
        this.supportMessage = '';
    }

    /**
     * Collects all response JSONs and sends them to Sentry as a data package.
     */
    async sendDataPackageToSupport(): Promise<void> {
        if (this.responses.length === 0) {
            console.warn('Cannot send data package: No responses available');
            this.trackTimeout(() => {
                this.error = 'No stored responses found to send.';
                this.successMessage = null;
            });
            return;
        }

        const Sentry = (window as any).Sentry;
        if (!Sentry) {
            console.error('Sentry is not available');
            this.trackTimeout(() => {
                this.error = 'Sentry is not available. Cannot send data package.';
                this.successMessage = null;
            });
            return;
        }

        // Close modal and trim the message (allow empty)
        this.showMessageModal = false;
        const trimmedMessage = this.supportMessage ? this.supportMessage.trim() : '';
        this.supportMessage = '';

        this.sendingDataPackage = true;
        this.error = null;
        this.successMessage = null;

        try {
            console.log(`ResponseSupportController: Collecting JSONs for ${this.responses.length} responses`);

            // Collect all JSONs
            const jsonData: Array<{ userUUID: string; json: string; error?: string }> = [];
            const errors: Array<{ userUUID: string; error: string }> = [];

            for (const response of this.responses) {
                try {
                    const json = await this.ResponseSupportBridgeClient.getResponseDataJson(response.userUUID);
                    if (json) {
                        jsonData.push({ userUUID: response.userUUID, json });
                        console.log(`ResponseSupportController: Collected JSON for ${response.userUUID}, length: ${json.length}`);
                    } else {
                        errors.push({ userUUID: response.userUUID, error: 'No JSON data returned' });
                    }
                } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to load JSON';
                    errors.push({ userUUID: response.userUUID, error: errorMessage });
                    console.error(`ResponseSupportController: Failed to load JSON for ${response.userUUID}:`, err);
                }
            }

            console.log(`ResponseSupportController: Collected ${jsonData.length} JSONs, ${errors.length} errors`);

            // Create a summary package
            const packageData = {
                timestamp: new Date().toISOString(),
                userMessage: trimmedMessage || undefined,
                totalResponses: this.responses.length,
                successfulJsonCount: jsonData.length,
                errorCount: errors.length,
                responses: jsonData.map(item => ({
                    userUUID: item.userUUID,
                    jsonLength: item.json.length
                })),
                errors: errors,
                allJsonData: jsonData.reduce((acc, item) => {
                    acc[item.userUUID] = item.json;
                    return acc;
                }, {} as Record<string, string>)
            };

            const packageJson = JSON.stringify(packageData, null, 2);
            const individualJsonFiles = jsonData.map(item => ({
                filename: `response-${item.userUUID}.json`,
                data: item.json,
                contentType: 'application/json'
            }));

            // Send to Sentry
            const errorTitle = trimmedMessage
                ? `Response Support Tool: ${trimmedMessage}`
                : `Response Support Tool: Data package with ${jsonData.length} response(s) and ${errors.length} error(s)`;
            const error = new Error(errorTitle);

            Sentry.withScope((scope: any) => {
                // Add the main package as an attachment
                if (scope.addAttachment) {
                    scope.addAttachment({
                        filename: 'response-data-package.json',
                        data: packageJson,
                        contentType: 'application/json'
                    });

                    // Add individual JSON files as attachments
                    individualJsonFiles.forEach(file => {
                        scope.addAttachment(file);
                    });
                }

                // Add summary as extra data
                scope.setExtra('totalResponses', this.responses.length);
                scope.setExtra('successfulJsonCount', jsonData.length);
                scope.setExtra('errorCount', errors.length);
                scope.setExtra('userUUIDs', jsonData.map(item => item.userUUID));
                if (trimmedMessage) {
                    scope.setExtra('userMessage', trimmedMessage);
                }

                scope.setContext('responseSupport', {
                    timestamp: packageData.timestamp,
                    totalResponses: packageData.totalResponses,
                    successfulJsonCount: packageData.successfulJsonCount,
                    errorCount: packageData.errorCount,
                    userUUIDs: jsonData.map(item => item.userUUID),
                    userMessage: trimmedMessage || undefined
                });

                // Set tags for easier filtering in Sentry
                scope.setTag('source', 'response-support-tool');
                scope.setTag('data-package', 'true');
                scope.setTag('response-count', jsonData.length.toString());

                Sentry.captureException(error);
            });

            console.log(`ResponseSupportController: Data package sent to Sentry with ${jsonData.length} JSONs`);

            // Show success message
            this.trackTimeout(() => {
                this.sendingDataPackage = false;
                this.error = null;
                this.successMessage = `Data package sent successfully! Sent ${jsonData.length} response(s) to support.`;

                // Clear success message after 5 seconds (track for $destroy cancellation)
                this.trackTimeout(() => {
                    this.successMessage = null;
                }, 5000);
            });
        } catch (err) {
            console.error('Failed to send data package to Sentry:', err);
            this.trackTimeout(() => {
                this.sendingDataPackage = false;
                this.successMessage = null;
                this.error = err instanceof Error ? err.message : 'Failed to send data package';
            });
        }
    }

    /**
     * Loads all pending recovery entries.
     */
    async loadPendingRecoveries(): Promise<void> {
        this.loadingRecoveries = true;

        try {
            const all = await this.ResponseSupportBridgeClient.listPendingRecoveries();
            this.trackTimeout(() => {
                this.pendingRecoveries = (all || []).filter((r: any) => r.phase !== 'recovered');
                this.recoveredResponses = (all || []).filter((r: any) => r.phase === 'recovered');
                this.loadingRecoveries = false;
            });
        } catch (err) {
            this.trackTimeout(() => {
                this.loadingRecoveries = false;
                this.error = err instanceof Error ? err.message : 'Failed to load pending recoveries';
                console.error('Error loading pending recoveries:', err);
            });
        }
    }

    /**
     * Retries a single pending recovery.
     * Error handling relies solely on the promise rejection — the listener
     * (which fires from the same SW message) is not used for per-entry error
     * display to avoid nondeterministic double-error messages.
     * @param id - The recovery entry ID to retry
     */
    async retryRecovery(id: number): Promise<void> {
        this.retryingId = id;
        this.error = null;
        this.successMessage = null;

        try {
            await this.ResponseSupportBridgeClient.retryPendingRecovery(id);
            // Success: reset state and refresh list
            this.trackTimeout(() => {
                this.retryingId = null;
                this.successMessage = `Recovery ${id} retried successfully.`;
                this.loadPendingRecoveries();
            });
        } catch (err) {
            this.trackTimeout(() => {
                this.retryingId = null;
                this.error = err instanceof Error ? err.message : 'Failed to retry recovery';
                console.error('Error retrying recovery:', err);
                this.loadPendingRecoveries();
            });
        }
    }

    /**
     * Retries all pending recoveries.
     * The listener-based loadPendingRecoveries is NOT called here because
     * the completion handler below refreshes the list once, avoiding N+1 calls.
     */
    async retryAllRecoveries(): Promise<void> {
        this.retryingAll = true;
        this.error = null;
        this.successMessage = null;

        try {
            const result = await this.ResponseSupportBridgeClient.retryAllPendingRecoveries();
            this.trackTimeout(() => {
                this.retryingAll = false;
                if (result.successCount > 0) {
                    this.successMessage = `Recovered ${result.successCount} upload(s)` +
                        (result.failCount > 0 ? `, ${result.failCount} failed` : '');
                } else if (result.failCount > 0) {
                    this.error = `All ${result.failCount} retry attempt(s) failed`;
                }
                this.loadPendingRecoveries();
            });
        } catch (err) {
            this.trackTimeout(() => {
                this.retryingAll = false;
                this.error = err instanceof Error ? err.message : 'Failed to retry all recoveries';
                console.error('Error retrying all recoveries:', err);
            });
        }
    }

    /**
     * Deletes a single pending recovery entry.
     * @param id - The recovery entry ID to delete
     */
    async deleteRecovery(id: number): Promise<void> {
        this.error = null;
        this.successMessage = null;

        try {
            await this.ResponseSupportBridgeClient.deletePendingRecovery(id);
            this.trackTimeout(() => {
                this.successMessage = `Recovery ${id} deleted.`;
                this.loadPendingRecoveries();
            });
        } catch (err) {
            this.trackTimeout(() => {
                this.error = err instanceof Error ? err.message : 'Failed to delete recovery';
                console.error('Error deleting recovery:', err);
            });
        }
    }

    /**
     * Formats a timestamp as a locale string for display in the template.
     * @param timestamp - Unix timestamp in milliseconds
     */
    formatDate(timestamp: number): string {
        return new Date(timestamp).toLocaleString();
    }

    /**
     * Navigates back to the PWA start screen.
     */
    goBack(): void {
        this.$location.path('/pwa-start');
    }
}

export default function registerResponseSupportController(controllers: angular.IModule): void {
    controllers.controller('ResponseSupportController', ResponseSupportController);
}
